import { action, observable, runInAction, makeObservable } from 'mobx';
import { UserCreateForm } from 'modules/user/forms';
import { BaseViewStore } from 'core/stores';
import { formInvalidate, isSuperAdminOrganization } from 'core/utils';
import { PrintNodeInfoStore } from 'modules/print-node-info/stores';
import { notificationError, generateRandomString } from 'core/utils';
import { CoreUserInfoStore } from 'modules/core-user-info/stores';
import { warehouseRoles, lookupRouteName, sellerRoles, roles as allRoles } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';
import { localStorageHandler } from 'core/services';
import { ApplicationStore } from 'platform/application/stores';

class UserCreateViewStore extends BaseViewStore {
	@observable loadingRoles = false;
	@observable showNotification = false;
	@observable hasPiiAccess = false;
	@observable canSetPii = false;
	@observable enableEmailNotification = false;
	@observable canSetEnableEmailNotification = false;
	@observable selectedLabelPrinter = null;
	@observable selectedPackingSlipPrinter = null;
	@observable printerOptions = [];
	@observable selectedRoles = [];
	@observable userRoles = [];
	notification = null;
	@observable isHavingAPISecret = false;
	loggedUser = null;
	@observable countryOptions = [];
	@observable isCountryLoading = false;
	timeouts = [];

	form = new UserCreateForm({
		onSuccess: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			} else {
				await this.createUser();
			}
		},
		onError: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			}
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.loggedUser = rootStore.authStore.user;
		this.parentView = rootStore.userListViewStore;
		this.listStore = rootStore.userListViewStore.listStore;
		this.applicationStore = new ApplicationStore(rootStore);
		this.printNodeInfoStore = new PrintNodeInfoStore(rootStore);
		this.coreUserInfoStore = new CoreUserInfoStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.notification = 'LOGINFORM.PRINTNODE.NOTIFICATIONS.REQUIRED';
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action
	initialize = async () => {
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		if (this.isSuperAdmin) {
			this.organizationDropdownStore = new BaasicDropdownViewStore({
				field: this.form.$('organization'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					embed: ['organizationType'],
				},
				onChangeCallback: this.fetchRoles,
			});
			this.form.$('organization').set('rules', 'required');
			const seller = localStorageHandler.get('selectedSeller');
			if (seller) {
				this.organizationDropdownStore.setValue(seller);
				this.userOrganizationInfo = this.organizationDropdownStore.value.extra;
				await this.organizationDropdownStore.reload();
			}
		} else {
			this.userOrganizationInfo = await this.applicationStore.getOrganizationInfo();
			await this.fetchRoles();
		}

		await this.getCountryLookupOptions();
		const piiRoles = _.find(
			this.loggedUser.roles,
			role =>
				role == warehouseRoles.warehouseAccountOwner ||
				role == sellerRoles.sellerAccountOwner ||
				role == allRoles.scanshipSuperAdministrator ||
				role == allRoles.superAdministrators
		);
		const loggedUserHasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		if (piiRoles && loggedUserHasPiiAccess) {
			this.canSetPii = true;
		} else {
			this.canSetPii = false;
		}
		const loggedUserRole = _.find(this.loggedUser.roles, role => role == warehouseRoles.warehouseAccountOwner);
		if (loggedUserRole) {
			this.canSetEnableEmailNotification = true;
		} else {
			this.canSetEnableEmailNotification = false;
		}
	};

	onRoleChange = selectedValue => {
		this.selectedRoles = selectedValue == null ? [] : selectedValue;
		this.form.$('roles').value = this.selectedRoles;
	};

	@action.bound
	onSelectChange = (selectedOption, field) => {
		field.value = selectedOption;
	};

	@action
	createUser = async () => {
		const { roles, ...fields } = this.form.values();

		const userRoles = _.map(this.selectedRoles, role => {
			return {
				name: role.label,
				id: role.value,
			};
		});

		let user = {
			isApproved: true,
			creationDate: new Date(),
			roles: userRoles,
			piiAccess: this.hasPiiAccess,
			apiSecret: fields.apiSecret,
			enableEmailNotification: this.enableEmailNotification,
			countryId: this.form.$('countryCode').value.value,
			organizationId: this.isSuperAdmin ? this.form.$('organization').value.value : null,
			...fields,
			userPhone: this.form.$('countryCode').value.extra.phoneCode + ' ' + this.form.$('userPhone').value,
		};

		delete user.countryCode;
		delete user.organization;
		if (
			this.form
				.$('password')
				.value.toLowerCase()
				.includes(this.form.$('userName').value.toLowerCase())
		) {
			this.form.invalidate('PASSWORDCONTAINSUNSERNAME');
		} else {
			this.suspend();
			try {
				await this.listStore.createUser(user);
				this.rootStore.notificationStore.success(['LOOKUPCREATE.USER', { user: user.userName }]);
				await this.parentView.findUsers();
				await this.parentView.closeModal();
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}

			this.resume();
		}
	};

	@action.bound
	fetchRoles = async () => {
		this.loadingRoles = true;
		this.form.$('roles').clear();
		this.selectedRoles = [];
		this.userRoles = [];

		if (this.isSuperAdmin && this.organizationDropdownStore.value) {
			this.userOrganizationInfo = this.organizationDropdownStore.value.extra;
		}

		if (this.userOrganizationInfo) {
			await this.listStore.fetchRoles(this.userOrganizationInfo.organizationType.name);
			_.forEach(this.listStore.roles, item => {
				this.userRoles.push({ value: item.id, label: item.name });
			});
		}

		runInAction(() => (this.loadingRoles = false));
	};

	@action.bound
	async getCountryLookupOptions() {
		await this.lookupStore.find({ page: 1, rpp: 300 }, lookupRouteName.country);
		this.countryOptions = _.map(this.lookupStore.data, item => {
			return { label: `${item.name} | ${item.phoneCode}`, value: item.id, extra: item };
		});
	}

	@action.bound
	getPrinters = async () => {
		try {
			const { apiKey } = this.form.values();
			if (apiKey.trim() == '') {
				this.showNotification = true;
			} else {
				this.showNotification = false;
				this.clearPrinters();
				await this.printNodeInfoStore.getPrinters(apiKey);
				if (this.printNodeInfoStore.response.printers.length > 0) {
					_.forEach(this.printNodeInfoStore.response.printers, item => {
						this.printerOptions.push({ value: item.id, label: item.name });
					});
					this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSFOUND');
				} else {
					this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSNOTFOUND');
				}
			}
		} catch (err) {
			if (err.data != null && !err.data.isSuccess) {
				this.rootStore.notificationStore.error('LOGINFORM.PRINTNODE.NOTIFICATIONS.INVALIDAPIKEY');
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	@action.bound
	onCheckPiiAccess = props => {
		if (props) {
			this.hasPiiAccess = props.currentTarget.checked;
		}
	};

	@action.bound
	onCheckEnableEmailNotification = props => {
		if (props) {
			this.enableEmailNotification = props.currentTarget.checked;
		}
	};

	@action.bound
	clearPrinters = () => {
		this.printerOptions = [];
		this.selectedLabelPrinter = null;
		this.form.$('labelPrinterId').value = '';
		this.form.$('labelPrinterName').clear();
		this.selectedPackingSlipPrinter = null;
		this.form.$('packingSlipPrinterId').value = '';
		this.form.$('packingSlipPrinterName').clear();
	};

	onChangeLabelPrinter = selectedValue => {
		this.selectedLabelPrinter = selectedValue;
		if (selectedValue != null) {
			this.form.$('labelPrinterId').value = selectedValue.value;
			this.form.$('labelPrinterName').value = selectedValue.label;
		} else {
			this.form.$('labelPrinterId').value = '';
			this.form.$('labelPrinterName').clear();
		}
	};

	onChangePackingSlipPrinter = selectedValue => {
		this.selectedPackingSlipPrinter = selectedValue;
		if (selectedValue != null) {
			this.form.$('packingSlipPrinterId').value = selectedValue.value;
			this.form.$('packingSlipPrinterName').value = selectedValue.label;
		} else {
			this.form.$('packingSlipPrinterId').value = '';
			this.form.$('packingSlipPrinterName').clear();
		}
	};

	@action.bound generateApiSecret() {
		this.isHavingAPISecret = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiSecret').set('value', radnomStringGenerated);
	}

	@action.bound
	async onMenuClose() {
		await this.getCountryLookupOptions();
	}

	onUserPhoneChange = value => {
		// Automatically correct user phone input 2.5 seconds after user stops typing
		const highestTimeoutId = setTimeout(() => {
			this.form.$('userPhone').resetValidation();
			this.timeouts.forEach(timeoutId => {
				if (timeoutId < highestTimeoutId) {
					clearTimeout(timeoutId);
				}
			});
			this.timeouts.push(
				setTimeout(() => {
					this.correctUserPhone();
				}, 2500)
			);
		}, 0);
	};

	@action.bound
	correctUserPhone = () => {
		const regex = /^\d+$/;
		let value = this.form.$('userPhone').value;
		if (!value.match(regex) && value) {
			value = value.replace(/\D/g, '');
			this.form.$('userPhone').value = value;
			return true;
		}
	};

	@action.bound
	checkUserPhone = () => {
		let value = this.form.$('userPhone').value;
		if (value) {
			const regex = /^\d+$/;
			var phone = this.form.$('countryCode').value.extra.phoneCode + value;
			if ((!value.match(regex) && value.length > 0) || (phone.length < 7 || phone.length > 15)) {
				return false;
			} else {
				return true;
			}
		}
	};
}

export default UserCreateViewStore;



// WEBPACK FOOTER //
// ./src/modules/user/stores/UserCreateViewStore.js